// UpdateMenu.jsx — FIXED: warehouse location dropdown at item level + per-variant level

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import ApiService from '../../../../services/apiService';
import ApiService2 from '../../../../../INVENTORY/services/apiService';
import ImageUpload from './imagUpload';
import { ReactComponent as Delete } from '../../../../assets/images/svgIcons/Delete.svg';
import { connect, useSelector } from 'react-redux';
import { getUserId } from '../../../../../Redux/selecters';

const norm = (v) =>
  String(v ?? '')
    .trim()
    .toLowerCase();

const getSupplierCode = (sup) =>
  (sup?.supplierCode ?? sup?.supplierVendor ?? sup?.supplierNo ?? sup?.code ?? sup?.supplierId ?? sup?.record_Id)?.toString() || '';

const parseVatPercent = (val) => {
  if (val === null || val === undefined) return null;
  const s = String(val);
  const m = s.match(/(\d+(\.\d+)?)/);
  return m ? Number(m[1]) : null;
};

const UpdateMenu = ({ Currency }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const val = state?.item;
  const UserId = useSelector(getUserId);

  const utcNoSeconds = () => new Date().toISOString();

  const isNullString = (v) => typeof v === 'string' && v.trim().toUpperCase() === 'NULL';
  const nullify = (v) => (isNullString(v) ? null : v);

  const toNumberSafe = (v, fallback = 0) => {
    const x = nullify(v);
    if (x === null || x === undefined || x === '') return fallback;
    const n = Number(x);
    return Number.isFinite(n) ? n : fallback;
  };

  const deepNullify = (input) => {
    if (Array.isArray(input)) return input.map(deepNullify);
    if (input && typeof input === 'object') {
      const out = {};
      for (const [k, v] of Object.entries(input)) out[k] = deepNullify(v);
      return out;
    }
    return nullify(input);
  };

  // ─── Form State ────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    record_Id: '',
    itemName: '',
    brand: '',
    description: '',
    retailPrice: '',
    wholesalePrice: '',
    costPrice: '',
    taxRate: '',
    category: '',
    subcategory: '',
    barcode: '',
    supplierVendor: '',
    supplierName: '',
    locationWarehouse: '', // ✅ item-level warehouse code e.g. "T-610340"
    weight: '',
    length: '',
    width: '',
    height: '',
    volume: '',
    productImage: '',
    inventoryUOM: '',
    packingType: '',
    thickness: '',
    reorderLevel: '',
    maxQTY: '',
    minQTY: '',
    shape: '',
    itemMasterVarients: [],
    conversitionRate: '',

    // react-select objects
    Vat: null,
    InventoryUom: null,
    Supplier: null,
    PackingType: null,
    Category: null,
    SubCategory: null,
    StockType: null,
    WarehouseLocation: null, // ✅ item-level react-select object

    images: [],
    imageUrl: '',
    convToInvUOM: '',
    size: '',
  });

  // ─── Dropdown Data ──────────────────────────────────────────────────────────
  const [vat, setvat] = useState([]);
  const [unitMeasurement, setunitMeasurement] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [packingType, setPackingType] = useState([]);
  const [stockType, setStockType] = useState([]);
  const [supplier, setSupplier] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]); // ✅

  // ─── Other State ────────────────────────────────────────────────────────────
  const [variants, setVariants] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);

  // ─── Fetch Dropdowns ────────────────────────────────────────────────────────
  const fetchDropdownData = useCallback(async () => {
    try {
      const apiService = new ApiService();
      const apiService2 = new ApiService2();
      const param = `?isActive=true`;

      const vatRes = await apiService.getApiParamater('GET_TAX', param);
      setvat(vatRes?.data?.result || []);

      const unitMeasurementResponse = await apiService2.getApiParamater('GET_EUNIT_MEASUREMENT', param);
      setunitMeasurement(unitMeasurementResponse?.data?.result || []);

      const SupplierRes = await apiService2.getApiParamater('GET_SUPPLIER', param);
      setSupplier(SupplierRes?.data?.result || []);

      // ✅ Fetch warehouse locations

      const categoryResponse = await apiService.getApiParamater('GET_CATEGORY_MASTER', param);
      setCategoryOptions(categoryResponse?.data?.result || []);

      const packingResponse = await apiService2.getApiParamater('GET_EPACKING', param);
      setPackingType(packingResponse?.data?.result || []);

      const stockTypeRes = await apiService2.getApiParamater('GET_STOCK_TYPE', param);
      setStockType(stockTypeRes?.data?.result || []);
      const warehouseRes = await apiService2.getApiParamater('GET_WAREHOUSE_MASTER', param);
      setWarehouseOptions(warehouseRes?.data?.result || []);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      toast.error('Failed to load dropdown data');
    }
  }, []);

  useEffect(() => {
    fetchDropdownData();
  }, [fetchDropdownData]);

  // ─── Init Form from val ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!val) return;

    setFormData((prev) => ({
      ...prev,
      record_Id: val.record_Id?.toString() || '',
      itemName: val.itemName || '',
      brand: val.brand ?? '',
      description: val.description || val.itemName || '',
      retailPrice: val.retailPrice ?? '',
      wholesalePrice: val.wholesalePrice ?? '',
      costPrice: val.costPrice ?? '',
      barcode: val.barcode ?? '',
      taxRate: val.taxRate ?? '',

      // ✅ store raw value from API so it can be matched during hydration
      locationWarehouse: val.locationWarehouse ?? '',

      category: val.category?.toString() || '',
      subcategory: val.subcategory?.toString() || '',
      supplierVendor: val.supplierVendor?.toString() || '',
      supplierName: val.supplierName ?? '',

      inventoryUOM: val.inventoryUOM ?? '',
      packingType: val.packingType ?? '',
      weight: val.weight ?? 0,
      length: val.length ?? 0,
      width: val.width ?? 0,
      height: val.height ?? 0,
      volume: val.volume ?? 0,
      thickness: val.thickness ?? 0,
      reorderLevel: val.reorderLevel ?? '',
      maxQTY: val.maxQTY ?? '',
      minQTY: val.minQTY ?? '',
      shape: val.shape ?? '',
      size: val.size ?? '',

      productImage: val.productImage ?? '',
      imageUrl: val.productImage ?? '',

      conversitionRate: val.conversitionRate ?? '',
      convToInvUOM: val.conversitionRate ?? '',

      itemMasterVarients: val.itemMasterVarients || [],
    }));

    setIsActive(val.itemStatus ?? val.active ?? true);
    setVariants(val.itemMasterVarients || []);
  }, [val]);

  // ─── Fetch Subcategories ────────────────────────────────────────────────────
  const handleSubcat = useCallback(async (catCode) => {
    if (!catCode) {
      setSubCategoryOptions([]);
      return;
    }
    try {
      const apiService = new ApiService();
      const param = `?isActive=true&Catcode=${catCode}`;
      const subCategoryResponse = await apiService.getApiParamater('GET_SUB_CATEGORY_MASTER', param);
      setSubCategoryOptions(subCategoryResponse?.data?.result || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      toast.error('Failed to fetch subcategories');
    }
  }, []);

  // ─── Hydrate Dropdowns in Edit Mode ────────────────────────────────────────
  useEffect(() => {
    if (!val) return;

    // CATEGORY
    if (categoryOptions?.length) {
      const foundCat = categoryOptions.find((c) => c?.catCode?.toString() === val?.category?.toString());
      if (foundCat) {
        setFormData((prev) => ({
          ...prev,
          Category: { label: foundCat.catName, value: foundCat },
          category: foundCat.catCode?.toString(),
        }));
        handleSubcat(foundCat.catCode?.toString());
      }
    }

    // SUPPLIER
    if (supplier?.length) {
      const itemSup = (val?.supplierVendor || '').toString();
      const foundSup = supplier.find((s) => getSupplierCode(s) === itemSup) || supplier.find((s) => norm(s?.supplierName) === norm(val?.supplierName));
      if (foundSup) {
        setFormData((prev) => ({
          ...prev,
          Supplier: { label: foundSup.supplierName, value: foundSup },
          supplierVendor: getSupplierCode(foundSup),
          supplierName: foundSup.supplierName || '',
        }));
      }
    }

    // UOM
    if (unitMeasurement?.length) {
      const foundUom = unitMeasurement.find((u) => norm(u?.description) === norm(val?.inventoryUOM));
      if (foundUom) {
        setFormData((prev) => ({
          ...prev,
          InventoryUom: { label: foundUom.description, value: foundUom },
          inventoryUOM: foundUom.description,
        }));
      }
    }

    // ✅ WAREHOUSE LOCATION — match by locationCode OR locationName against val.locationWarehouse
    // (e.g. val.locationWarehouse = "T-610340"; adjust field names to match your API response)
    if (warehouseOptions?.length) {
      const rawWh = val?.locationWarehouse?.toString() || '';
      const foundWh = warehouseOptions.find(
        (w) =>
          w?.locationCode?.toString() === rawWh ||
          w?.locationName?.toString() === rawWh ||
          w?.whCode?.toString() === rawWh || // fallback field names
          w?.description?.toString() === rawWh,
      );
      if (foundWh) {
        setFormData((prev) => ({
          ...prev,
          WarehouseLocation: {
            label: foundWh.locationName ?? foundWh.description ?? foundWh.whCode ?? rawWh,
            value: foundWh,
          },
          locationWarehouse: foundWh.locationCode?.toString() || foundWh.whCode?.toString() || foundWh.locationName || rawWh,
        }));
      } else if (rawWh) {
        // ✅ Fallback: show the raw code as a label so the field is not blank
        setFormData((prev) => ({
          ...prev,
          WarehouseLocation: { label: rawWh, value: null },
          locationWarehouse: rawWh,
        }));
      }
    }

    // PACKING TYPE
    if (packingType?.length) {
      const foundPack = packingType.find((p) => norm(p?.description) === norm(val?.packingType));
      if (foundPack) {
        setFormData((prev) => ({
          ...prev,
          PackingType: { label: foundPack.description, value: foundPack },
          packingType: foundPack.description,
        }));
      }
    }

    // VAT
    if (vat?.length) {
      const taxPercent = val?.taxRate ?? parseVatPercent(val?.itemMasterVarients?.[0]?.vat);
      const foundVat = vat.find((v) => Number(v?.taxPercentage) === Number(taxPercent));
      if (foundVat) {
        setFormData((prev) => ({
          ...prev,
          Vat: { label: `${foundVat.name} ${foundVat.taxPercentage} %`, value: foundVat },
          taxRate: foundVat.taxPercentage,
        }));
      }
    }
  }, [val, categoryOptions, supplier, unitMeasurement, packingType, vat, warehouseOptions, handleSubcat]);

  // Hydrate SubCategory after subCategoryOptions loaded
  useEffect(() => {
    if (!val?.subcategory || !subCategoryOptions?.length) return;
    const found = subCategoryOptions.find((sc) => sc?.subcatcode?.toString() === val?.subcategory?.toString());
    if (found) {
      setFormData((prev) => ({
        ...prev,
        SubCategory: { label: found.subcatname, value: found },
        subcategory: found.subcatcode?.toString(),
      }));
    }
  }, [val, subCategoryOptions]);

  // ─── Input Handlers ─────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'convToInvUOM' && { conversitionRate: value }),
      ...(name === 'conversitionRate' && { convToInvUOM: value }),
    }));
  };

  const handlevatChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      Vat: selectedOption,
      taxRate: selectedOption?.value?.taxPercentage ?? '',
    }));
  };

  const handleIUtMeasurementChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      InventoryUom: selectedOption,
      inventoryUOM: selectedOption?.label || '',
    }));
  };

  const handlePackingTypeChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      PackingType: selectedOption,
      packingType: selectedOption?.label || '',
    }));
  };

  const handleSupplierChange = (selectedOption) => {
    const sup = selectedOption?.value;
    setFormData((prev) => ({
      ...prev,
      Supplier: selectedOption,
      supplierVendor: getSupplierCode(sup),
      supplierName: sup?.supplierName || '',
    }));
  };

  const handleCategoryChange = (selectedOption) => {
    const cat = selectedOption?.value;
    const catCode = cat?.catCode?.toString() || '';
    setFormData((prev) => ({
      ...prev,
      Category: selectedOption,
      category: catCode,
      SubCategory: null,
      subcategory: '',
    }));
    handleSubcat(catCode);
  };

  const handleSubCategoryChange = (selectedOption) => {
    const sub = selectedOption?.value;
    setFormData((prev) => ({
      ...prev,
      SubCategory: selectedOption,
      subcategory: sub?.subcatcode?.toString() || '',
    }));
  };

  // ✅ Item-level warehouse handler
  const handleWarehouseChange = (selectedOption) => {
    const wh = selectedOption?.value;
    setFormData((prev) => ({
      ...prev,
      WarehouseLocation: selectedOption,
      locationWarehouse: wh?.locationCode?.toString() || wh?.whCode?.toString() || wh?.locationName || '',
    }));
  };

  const handleSwitchChange = () => setIsActive((p) => !p);

  // ─── Variant Helpers ─────────────────────────────────────────────────────────

  // ✅ Build react-select value for a per-variant warehouse field
  const getVariantWarehouseValue = (variant) => {
    const raw = variant?.warehouseLocation?.toString() || '';
    if (!raw) return null;

    const matched = warehouseOptions.find(
      (w) => w?.locationCode?.toString() === raw || w?.whCode?.toString() === raw || w?.locationName?.toString() === raw || w?.description?.toString() === raw,
    );

    if (matched) {
      return {
        label: matched.locationName ?? matched.description ?? matched.whCode ?? raw,
        value: matched,
      };
    }
    // Fallback: show raw value so field is not blank
    return { label: raw, value: null };
  };

  // ✅ Extract the code to store back into variant.warehouseLocation
  const extractWarehouseCode = (wh) => wh?.locationCode?.toString() || wh?.whCode?.toString() || wh?.locationName || '';

  const handleVariantChange = (index, field, value) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    setVariants(updated);
    setFormData((prev) => ({ ...prev, itemMasterVarients: updated }));
  };

  const handleAddVariant = () => {
    const newVariant = {
      recordId: 0,
      itemNo: formData.itemCode || formData.barcode || '',
      itemDescription: formData.description || formData.itemName || '',
      purchasePrice: 0,
      salePrice: 0,
      stock: 0,
      rol: 0,
      minLevel: 0,
      maxLevel: 0,
      purchaseUnit: 0,
      saleUnit: 0,
      brand: formData.brand || '0',
      supplier: formData.supplierVendor || '',
      remarks1: '',
      remarks2: '',
      warranty: '0',
      vat: formData?.Vat?.label || 'VAT @0%',
      stockType: 'INV',
      category: formData.category || '',
      subCategory: formData.subcategory || '',
      // ✅ Seed new variants with the item-level warehouse
      warehouseLocation: formData.locationWarehouse || '',
      binLocation: '0',
      unitMeasurement: formData.InventoryUom || '',
      baseUnit: formData.InventoryUom || '',
      packingType: formData.packingType || '',
      size: formData.size || '',
      shape: formData.shape || '',
      weight: String(formData.weight ?? ''),
      barcode: formData.barcode || null,
      inventory: 'True',
      fixedAsset: 'False',
      sales: 'False',
      purchased: 'True',
      lastPriceUpdated: utcNoSeconds(),
      exp7: formData.record_Id?.toString() || null,
      exp9: '1',
      createdBy: UserId?.toString() || '',
      createdOn: utcNoSeconds(),
      modifiedBy: UserId?.toString() || '',
      modifiedOn: utcNoSeconds(),
      primaryUOM: formData?.InventoryUom || '0',
      convToInvUOM: Number(formData.conversitionRate || 1),
      convToPriUOM: parseFloat(formData.conversitionRate || 1),
      length: String(formData.length ?? ''),
      width: String(formData.width ?? ''),
      height: String(formData.height ?? ''),
      volume: String(formData.volume ?? ''),
      thickness: String(formData.thickness ?? ''),
    };

    const updated = [...variants, newVariant];
    setVariants(updated);
    setFormData((prev) => ({ ...prev, itemMasterVarients: updated }));
  };

  const handleDeleteVariant = (index) => {
    const updated = variants.filter((_, i) => i !== index);
    setVariants(updated);
    setFormData((prev) => ({ ...prev, itemMasterVarients: updated }));
  };

  // ─── Image Upload ────────────────────────────────────────────────────────────
  const UploadImage = async () => {
    const hasNewFiles = Array.isArray(formData.images) && formData.images.some((img) => img?.file);
    if (!hasNewFiles) return formData.productImage || formData.imageUrl || '';

    try {
      const apiService = new ApiService();
      const fd = new FormData();
      for (const image of formData.images) {
        if (image?.file) fd.append('Images', image.file);
      }
      const response = await apiService.postMultiPart('UPLOAD_IMG', fd);
      if (response?.data?.statusCode === 201) return response?.data?.result || '';
      toast.error(response?.data?.msg || 'Image upload failed');
      return formData.productImage || formData.imageUrl || '';
    } catch (e) {
      toast.error('Something went wrong with the image. Please try again!');
      return formData.productImage || formData.imageUrl || '';
    }
  };

  const handleImagesSelect = (images) => {
    setFormData((prev) => ({
      ...prev,
      images,
      productImage: images?.[0]?.url || prev.productImage || '',
    }));
  };

  const handleImageRemove = () => {
    setFormData((prev) => ({ ...prev, productImage: '', images: [] }));
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const imageUrl = await UploadImage();

      if (!formData.record_Id) {
        toast.error('record_Id missing. Cannot update.');
        return;
      }

      const body = {
        record_Id: parseInt(formData.record_Id) || 0,
        sku: val?.sku || formData.barcode || '',
        itemCode: val?.itemCode || formData.barcode || '',
        itemName: formData.itemName || '',
        description: formData.description || '',
        brand: formData.brand || '',
        modelNumber: val?.modelNumber || null,
        barcode: formData.barcode || '',
        retailPrice: Number(formData.retailPrice) || 0,
        wholesalePrice: Number(formData.wholesalePrice) || 0,
        costPrice: Number(formData.costPrice) || 0,
        discountPrice: val?.discountPrice || 0,
        priceHistory: val?.priceHistory || null,
        currency: val?.currency || formData.currency || Currency?.currency?.name || null,

        taxRate: formData.taxRate === '' ? 0 : Number(formData.taxRate),
        taxCategory: val?.taxCategory || null,

        stockQuantity: val?.stockQuantity || '0.0000',
        minStockLevel: Number(formData.minQTY) || 0,
        maxStockLevel: Number(formData.maxQTY) || 0,
        restockDate: val?.restockDate ? utcNoSeconds(new Date(val.restockDate)) : null,

        supplierVendor: formData.Supplier ? getSupplierCode(formData.Supplier.value) : formData.supplierVendor || '',
        supplierName: formData.Supplier?.value?.supplierName || formData.supplierName || null,

        // ✅ Use selected warehouse from dropdown; fall back to raw string value
        locationWarehouse:
          formData?.WarehouseLocation?.label || formData?.locationWarehouse || formData?.WarehouseLocation || formData?.locationWarehouse?.label || '',

        weight: Number(formData.weight) || 0,
        length: Number(formData.length) || 0,
        width: Number(formData.width) || 0,
        height: Number(formData.height) || 0,
        volume: Number(formData.volume) || 0,

        productImage: imageUrl || null,
        additionalImages: val?.additionalImages || null,

        color: val?.color || null,
        size: formData.size || val?.size || '',
        material: val?.material || '',
        style: val?.style || '',

        manufacturerPartNumber: val?.manufacturerPartNumber || null,
        leadTime: val?.leadTime ? utcNoSeconds(new Date(val.leadTime)) : null,
        saleStartDate: val?.saleStartDate ? utcNoSeconds(new Date(val.saleStartDate)) : null,
        saleEndDate: val?.saleEndDate ? utcNoSeconds(new Date(val.saleEndDate)) : null,

        promotionCodes: val?.promotionCodes || null,
        specialOffers: val?.specialOffers || null,

        shippingWeight: val?.shippingWeight || null,
        shippingDimensions: val?.shippingDimensions || null,
        shippingClass: val?.shippingClass || null,
        shippingRestrictions: val?.shippingRestrictions || null,

        ratingReviews: val?.ratingReviews || null,
        returnPolicy: val?.returnPolicy || null,
        expirationDate: val?.expirationDate ? utcNoSeconds(new Date(val.expirationDate)) : null,
        availabilityStatus: val?.availabilityStatus || null,

        itemStatus: isActive,
        active: isActive,

        category: formData.Category?.value?.catCode?.toString() || formData.category || '',
        subcategory: formData.SubCategory?.value?.subcatcode?.toString() || formData.subcategory || '',

        productTags: val?.productTags || null,

        inventoryUOM: formData.inventoryUOM || '',
        packingType: formData.packingType || '',
        thickness: Number(formData.thickness) || 0,
        reorderLevel: Number(formData.reorderLevel) || 0,
        maxQTY: formData.maxQTY === '' || formData.maxQTY === null ? 0 : Number(formData.maxQTY),
        minQTY: formData.minQTY === '' || formData.minQTY === null ? 0 : Number(formData.minQTY),
        shape: formData.shape || '',

        conversitionRate: formData.conversitionRate === '' ? 0 : Number(formData.conversitionRate),

        itemMasterDetails: (variants?.length ? variants : val?.itemMasterVarients || []).map((v) => ({
          recordId: toNumberSafe(v.recordId, 0),
          itemNo: nullify(v.itemNo) ?? '',
          itemDescription: nullify(v.itemDescription) ?? '',
          purchasePrice: toNumberSafe(v.purchasePrice, 0),
          salePrice: toNumberSafe(v.salePrice, 0),
          stock: toNumberSafe(v.stock, 0),
          rol: toNumberSafe(v.rol, 0),
          minLevel: toNumberSafe(v.minLevel, 0),
          maxLevel: toNumberSafe(v.maxLevel, 0),
          vat: nullify(v.vat) ?? null,
          // ✅ Each variant carries its own warehouseLocation code
          warehouseLocation: nullify(v.warehouseLocation) ?? '',
          createdBy: nullify(v.createdBy) ?? (UserId?.toString() || null),
          createdOn: v.createdOn ? utcNoSeconds() : utcNoSeconds(),
          modifiedBy: UserId?.toString() || null,
          modifiedOn: utcNoSeconds(),
          inventory: 'true',
          exp9: '1',
          stockType: 'INV',
          inventoryUOM: formData.inventoryUOM || '',
          packingType: formData.packingType || '',
          unitMeasurement: formData.InventoryUom.label || '',
          // baseUnit: formData.pack || '',
          // primaryUOM: formData.packingType || '',
        })),
      };

      const apiService = new ApiService();
      console.log('UPDATE BODY:', body);
      console.log("HAS 'NULL'?:", JSON.stringify(body).includes('"NULL"'));
      const cleanedBody = deepNullify(body);
      const response = await apiService.put('UPDATE_ITEM_MASTER', cleanedBody);

      if (response?.data?.statusCode === 200 || response?.data?.statusCode === 201) {
        toast.success('Item updated successfully');
        navigate('/admin/menu');
      } else {
        toast.warning(response?.data?.message || 'Update failed');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Something went wrong. Please try again!');
    } finally {
      setLoading(false);
    }
  };

  // ─── Build warehouse options once (memoised inline) ──────────────────────────
  const warehouseSelectOptions = warehouseOptions.map((wh) => ({
    value: wh,
    label: wh.locationName ?? wh.description ?? wh.whCode ?? wh.locationCode ?? '',
  }));

  // ─── UI ──────────────────────────────────────────────────────────────────────
  return (
    <div className="p-5">
      <div className="p-5">
        {/* ── Row 1: Basic Info + Image/Physical ── */}
        <div className="row rows">
          {/* Basic Information */}
          <div className="col-lg-6 col6 creatBox">
            <div className="headtext">Basic Information</div>
            <div className="orngbox">
              {/* Item Name */}
              <div>
                <label htmlFor="itemName" className="form-label texthead">
                  Item Name *
                </label>
                <input type="text" className="p-2 form-control" id="itemName" name="itemName" value={formData.itemName} onChange={handleInputChange} required />
              </div>

              {/* Brand */}
              <div className="py-3">
                <label htmlFor="brand" className="form-label texthead">
                  Brand *
                </label>
                <input type="text" className="p-2 form-control" id="brand" name="brand" value={formData.brand} onChange={handleInputChange} required />
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="form-label texthead">
                  Category *
                </label>
                <Select
                  value={formData.Category}
                  onChange={handleCategoryChange}
                  options={categoryOptions?.map((cat) => ({ value: cat, label: cat.catName }))}
                  placeholder="Select Category"
                />
              </div>

              {/* Subcategory */}
              <div className="py-3">
                <label htmlFor="subcategory" className="form-label texthead">
                  Subcategory *
                </label>
                <Select
                  value={formData.SubCategory}
                  onChange={handleSubCategoryChange}
                  options={subCategoryOptions?.map((sub) => ({ value: sub, label: sub.subcatname }))}
                  placeholder="Select Subcategory"
                />
              </div>

              {/* Supplier */}
              <div>
                <label htmlFor="supplierVendor" className="form-label texthead">
                  Supplier *
                </label>
                <Select
                  value={formData.Supplier}
                  onChange={handleSupplierChange}
                  options={supplier?.map((sup) => ({ value: sup, label: sup?.supplierName }))}
                  placeholder="Select Supplier"
                />
              </div>

              {/* ✅ Warehouse Location (item-level) */}
              <div className="py-3">
                <label htmlFor="locationWarehouse" className="form-label texthead">
                  Warehouse Location
                </label>
                <Select
                  value={formData.WarehouseLocation}
                  onChange={handleWarehouseChange}
                  options={warehouseSelectOptions}
                  placeholder="Select Warehouse Location"
                  isClearable
                />
              </div>

              {/* Barcode */}
              <div>
                <label htmlFor="barcode" className="form-label texthead">
                  Barcode
                </label>
                <input type="text" className="p-2 form-control" id="barcode" name="barcode" value={formData.barcode} onChange={handleInputChange} />
              </div>

              {/* Description */}
              <div className="py-3">
                <label htmlFor="description" className="form-label texthead">
                  Description *
                </label>
                <textarea
                  className="p-2 form-control"
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  required
                />
              </div>

              {/* VAT */}
              <div className="py-3">
                <label htmlFor="taxRate" className="form-label texthead">
                  VAT *
                </label>
                <Select
                  value={formData.Vat}
                  onChange={handlevatChange}
                  options={vat?.map((vatItem) => ({
                    value: vatItem,
                    label: `${vatItem?.name} ${vatItem?.taxPercentage} %`,
                  }))}
                  placeholder="Select VAT"
                />
              </div>

              {/* Active Toggle */}
              <div className="py-3 d-flex justify-content-between">
                <div className="d-flex text-center">
                  <div className="headtes p-2">{isActive ? 'Active' : 'Inactive'}</div>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" checked={isActive} onChange={handleSwitchChange} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Image + Physical Properties */}
          <div className="col-lg-6 col6">
            <div className="creatBox">
              <div className="headtext">Upload Item Picture</div>
              <div>
                <ImageUpload onImagesSelect={handleImagesSelect} onImageRemove={handleImageRemove} formData={formData} />
              </div>
            </div>

            <div className="mt-4">
              <div className="headtext">Physical Properties</div>
              <div className="creatBox">
                <div className="row">
                  <div className="col-md-6">
                    <label htmlFor="weight" className="form-label texthead">
                      Weight
                    </label>
                    <input type="number" className="form-control" id="weight" name="weight" value={formData.weight} onChange={handleInputChange} />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="volume" className="form-label texthead">
                      Volume
                    </label>
                    <input type="number" className="form-control" id="volume" name="volume" value={formData.volume} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-4">
                    <label htmlFor="length" className="form-label texthead">
                      Length
                    </label>
                    <input type="number" className="form-control" id="length" name="length" value={formData.length} onChange={handleInputChange} />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="width" className="form-label texthead">
                      Width
                    </label>
                    <input type="number" className="form-control" id="width" name="width" value={formData.width} onChange={handleInputChange} />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="height" className="form-label texthead">
                      Height
                    </label>
                    <input type="number" className="form-control" id="height" name="height" value={formData.height} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-6">
                    <label htmlFor="thickness" className="form-label texthead">
                      Thickness
                    </label>
                    <input type="number" className="form-control" id="thickness" name="thickness" value={formData.thickness} onChange={handleInputChange} />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="shape" className="form-label texthead">
                      Shape
                    </label>
                    <input type="text" className="form-control" id="shape" name="shape" value={formData.shape} onChange={handleInputChange} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Row 2: Pricing + Inventory ── */}
        <div className="row rows pt-5">
          {/* Pricing */}
          <div className="col-lg-6 col6 creatBox">
            <div className="headtext">Pricing Information</div>
            <div className="orngbox">
              <div>
                <label htmlFor="retailPrice" className="form-label texthead">
                  Retail Price *
                </label>
                <input
                  type="number"
                  className="p-2 form-control"
                  id="retailPrice"
                  name="retailPrice"
                  value={formData.retailPrice}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="py-3">
                <label htmlFor="wholesalePrice" className="form-label texthead">
                  Wholesale Price *
                </label>
                <input
                  type="number"
                  className="p-2 form-control"
                  id="wholesalePrice"
                  name="wholesalePrice"
                  value={formData.wholesalePrice}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="py-3">
                <label htmlFor="taxRate" className="form-label texthead">
                  Tax Rate (%) *
                </label>
                <input type="number" className="p-2 form-control" id="taxRate" name="taxRate" value={formData.taxRate} disabled />
              </div>
            </div>
          </div>

          {/* Inventory */}
          <div className="col-lg-6 col6">
            <div className="creatBox">
              <div className="headtext">Inventory Information</div>
              <div className="orngbox">
                <div>
                  <label htmlFor="inventoryUOM" className="form-label texthead">
                    Inventory UOM *
                  </label>
                  <Select
                    value={formData.InventoryUom}
                    onChange={handleIUtMeasurementChange}
                    options={unitMeasurement?.map((uom) => ({ value: uom, label: uom.description }))}
                    placeholder="Select UOM"
                  />
                </div>

                <div className="py-3">
                  <label htmlFor="packingType" className="form-label texthead">
                    Packing Type
                  </label>
                  <Select
                    value={formData.PackingType}
                    onChange={handlePackingTypeChange}
                    options={packingType?.map((packing) => ({ value: packing, label: packing.description }))}
                    placeholder="Select Packing Type"
                  />
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <label htmlFor="minQTY" className="form-label texthead">
                      Min QTY
                    </label>
                    <input type="number" className="form-control" id="minQTY" name="minQTY" value={formData.minQTY} onChange={handleInputChange} />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="maxQTY" className="form-label texthead">
                      Max QTY
                    </label>
                    <input type="number" className="form-control" id="maxQTY" name="maxQTY" value={formData.maxQTY} onChange={handleInputChange} />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="reorderLevel" className="form-label texthead">
                      Reorder Level
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="reorderLevel"
                      name="reorderLevel"
                      value={formData.reorderLevel}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-6">
                    <label htmlFor="convToInvUOM" className="form-label texthead">
                      Conversion Rate
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="convToInvUOM"
                      name="convToInvUOM"
                      value={formData.convToInvUOM}
                      onChange={handleInputChange}
                      placeholder="1"
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="size" className="form-label texthead">
                      Size
                    </label>
                    <input type="text" className="form-control" id="size" name="size" value={formData.size} onChange={handleInputChange} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Row 3: Variants ── */}
        <div className="row rows pt-5">
          <div className="col-lg-12">
            <div className="creatBox">
              <div className="headtext">Product Variants</div>
              <div className="orngbox">
                <button type="button" className="btn btn-primary mb-3" onClick={handleAddVariant}>
                  Add Variant
                </button>

                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Item No</th>
                        <th>Description</th>
                        <th>Purchase Price</th>
                        <th>Sale Price</th>
                        <th>Stock</th>
                        <th>VAT</th>
                        {/* <th>Warehouse Location</th> */}
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {variants?.map((variant, index) => (
                        <tr key={variant?.recordId || index}>
                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={variant.itemNo || ''}
                              onChange={(e) => handleVariantChange(index, 'itemNo', e.target.value)}
                            />
                          </td>

                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={variant.itemDescription || ''}
                              onChange={(e) => handleVariantChange(index, 'itemDescription', e.target.value)}
                            />
                          </td>

                          <td>
                            <input
                              type="number"
                              className="form-control"
                              value={variant.purchasePrice ?? ''}
                              onChange={(e) => handleVariantChange(index, 'purchasePrice', Number(e.target.value) || 0)}
                            />
                          </td>

                          <td>
                            <input
                              type="number"
                              className="form-control"
                              disabled
                              value={variant.salePrice ?? ''}
                              onChange={(e) => handleVariantChange(index, 'salePrice', Number(e.target.value) || 0)}
                            />
                          </td>

                          <td>
                            <input
                              type="number"
                              className="form-control"
                              value={variant.stock ?? ''}
                              onChange={(e) => handleVariantChange(index, 'stock', Number(e.target.value) || 0)}
                            />
                          </td>

                          <td>
                            <input
                              type="text"
                              className="form-control"
                              value={variant.vat || ''}
                              onChange={(e) => handleVariantChange(index, 'vat', e.target.value)}
                            />
                          </td>

                          {/* ✅ Per-variant warehouse dropdown — reads/writes variant.warehouseLocation */}
                          {/* <td style={{ minWidth: '200px' }}>
                            <Select
                              value={getVariantWarehouseValue(variant)}
                              onChange={(selectedOption) =>
                                handleVariantChange(index, 'warehouseLocation', selectedOption ? extractWarehouseCode(selectedOption.value) : '')
                              }
                              options={warehouseSelectOptions}
                              placeholder="Select Warehouse"
                              isClearable
                            />
                          </td> */}

                          <td>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDeleteVariant(index)}>
                              <Delete />
                            </button>
                          </td>
                        </tr>
                      ))}

                      {!variants?.length && (
                        <tr>
                          <td colSpan={8} className="text-center text-muted">
                            No variants
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Submit ── */}
        <div className="end pt-4">
          <button className="addItem-btn" type="button" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Processing...' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStatetoProps = (state) => state;
export default connect(mapStatetoProps)(UpdateMenu);
